import { useCallback, useEffect, useRef, useState, type PointerEvent, type ReactNode } from 'react';
import { IconButton } from './Button.js';

export type SheetVariant = 'sheet' | 'full' | 'alert';

/**
 * A pull this far (px) or this fast at the release (px/ms) closes a bottom sheet, a shorter and slower one lets it
 * spring back: M3's thresholds (compose material3 SheetDefaults: 56 dp, 125 dp/s). The speed is taken over the last
 * PULL_SPEED_MS of the pull, not between the last two events.
 */
const PULL_CLOSE = 56;
const FLICK_CLOSE = 0.125;
const PULL_SPEED_MS = 80;
/** movement before a press on the handle counts as a pull, so taps on the header buttons still work */
const PULL_START = 6;
/** the exit animation is 150 ms (--dur-fast-effects); unmount at the latest after this, also without animations */
const CLOSE_FALLBACK_MS = 400;

/**
 * A sheet the parent closes itself (a button inside: "Spülen starten", "Speichern", "Abbrechen") is unmounted at once,
 * so it vanished in one frame together with its scrim (22.10.2026). On that unmount a silent copy takes its place for
 * the exit animation: inert, without ids and test ids, over a scrim that fades out; then both go.
 */
function exitCopy(dialog: HTMLDialogElement, body: HTMLElement | null) {
  const copy = dialog.cloneNode(true) as HTMLDialogElement;
  for (const el of [copy, ...copy.querySelectorAll('[id], [data-testid]')]) {
    el.removeAttribute('id');
    el.removeAttribute('data-testid');
  }
  copy.inert = true;
  copy.setAttribute('aria-hidden', 'true');
  copy.classList.add('sheet-exit');
  copy.setAttribute('data-closing', '');
  const scrim = dialog.classList.contains('sheet-full') ? null : document.createElement('div');
  if (scrim) scrim.className = 'sheet-exit-scrim';
  document.body.append(...(scrim ? [scrim, copy] : [copy]));
  const copyBody = copy.querySelector<HTMLElement>('.sheet-body');
  if (copyBody && body) copyBody.scrollTop = body.scrollTop;
  let gone = false;
  const remove = () => {
    if (gone) return;
    gone = true;
    copy.remove();
    scrim?.remove();
  };
  copyBody?.addEventListener('animationend', remove);
  window.setTimeout(remove, CLOSE_FALLBACK_MS);
}

/**
 * Modal surface on the native <dialog> (top layer, focus trap, Escape, inert background):
 * - sheet: slides up from the bottom (account, choices); pulled down at the handle or header it closes
 * - full: full screen flow (pairing)
 * - alert: short confirmation (flush, unpair)
 * Mount it only while it is open; closing is the parent's state (onClose). Closing from inside (close button,
 * backdrop, Escape, pull) plays the exit animation first and then calls onClose; when the parent unmounts it directly
 * (an action button inside), a silent copy plays the same exit (exitCopy).
 */
export function Sheet({
  title,
  titleId,
  variant = 'sheet',
  onClose,
  closeLabel,
  closeTestId,
  headerAside,
  hideTitle = false,
  children,
  testId,
}: {
  title: ReactNode;
  titleId: string;
  variant?: SheetVariant;
  onClose: () => void;
  /** label of the close button; without it there is no close button in the header */
  closeLabel?: string;
  closeTestId?: string;
  /** right side of the header, e.g. "1/2" */
  headerAside?: ReactNode;
  hideTitle?: boolean;
  children: ReactNode;
  testId?: string;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  const [closing, setClosing] = useState(false);
  /** closed with its own exit animation (close button, backdrop, Escape, pull): no copy on unmount */
  const exited = useRef(false);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    const dialog = ref.current;
    const body = bodyRef.current;
    if (!dialog) return;
    if (!dialog.open && typeof dialog.showModal === 'function') dialog.showModal();
    // the sheet itself takes the focus, not its first button: on the phone that button showed a focus ring. A field
    // marked data-autofocus takes it instead (React's autoFocus ran while the dialog was still closed and found
    // nothing to focus; 23.10.2026)
    const first = dialog.querySelector<HTMLElement>('[data-autofocus]');
    (first ?? bodyRef.current)?.focus({ preventScroll: true });
    // the frame that builds the sheet paints it below the edge; it starts moving in the next one (data-in, CSS)
    let raf = 0;
    let fallback = 0;
    const start = () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(fallback);
      dialog.setAttribute('data-in', '');
    };
    raf = requestAnimationFrame(() => {
      raf = requestAnimationFrame(start);
    });
    fallback = window.setTimeout(start, 100);
    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(fallback);
      // shown (data-in) and closed by the parent: the copy plays the exit (not the first double mount of StrictMode)
      if (!exited.current && dialog.hasAttribute('data-in')) exitCopy(dialog, body);
      if (dialog.open) dialog.close();
    };
  }, []);

  const close = useCallback(() => {
    // after a pull the body holds animation: none; the exit animation of the stylesheet must apply again
    bodyRef.current?.style.removeProperty('animation');
    setClosing(true);
  }, []);

  // the exit animation of the body ends (or the fallback runs out), then the parent unmounts the sheet
  useEffect(() => {
    if (!closing) return;
    const body = bodyRef.current;
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      exited.current = true;
      onCloseRef.current();
    };
    const onEnd = (e: AnimationEvent) => {
      if (e.target === body) finish();
    };
    body?.addEventListener('animationend', onEnd);
    const timer = window.setTimeout(finish, CLOSE_FALLBACK_MS);
    return () => {
      body?.removeEventListener('animationend', onEnd);
      window.clearTimeout(timer);
    };
  }, [closing]);

  // Pulling a bottom sheet down: the body follows the finger, a long or fast pull closes it from where it is,
  // a short one springs back.
  const pull = useRef<{ id: number; y0: number; trail: [number, number][]; on: boolean } | null>(null);
  const swallowClick = useRef(false);
  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    const body = bodyRef.current;
    if (variant !== 'sheet' || closing || !body || e.button !== 0) return;
    const target = e.target as Element;
    const onHandle =
      target.closest('.sheet-grab, .sheet-head') !== null ||
      (target === body && e.clientY - body.getBoundingClientRect().top < 40);
    if (!onHandle) return;
    pull.current = { id: e.pointerId, y0: e.clientY, trail: [[e.timeStamp, e.clientY]], on: false };
  };
  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    const p = pull.current;
    const body = bodyRef.current;
    if (!p || !body || p.id !== e.pointerId) return;
    const dy = Math.max(0, e.clientY - p.y0);
    if (!p.on) {
      if (dy < PULL_START) return;
      p.on = true;
      body.setPointerCapture(e.pointerId);
      body.style.animation = 'none';
      body.style.transition = 'none';
    }
    p.trail.push([e.timeStamp, e.clientY]);
    while (p.trail.length > 2 && e.timeStamp - (p.trail[0]?.[0] ?? 0) > PULL_SPEED_MS) p.trail.shift();
    body.style.transform = `translateY(${dy}px)`;
  };
  const onPointerEnd = (e: PointerEvent<HTMLDivElement>) => {
    const p = pull.current;
    const body = bodyRef.current;
    pull.current = null;
    if (!p || !body || p.id !== e.pointerId || !p.on) return;
    swallowClick.current = true;
    const [t0, y0] = p.trail[0] ?? [e.timeStamp, e.clientY];
    const speed = e.timeStamp > t0 ? (e.clientY - y0) / (e.timeStamp - t0) : 0;
    if (e.clientY - p.y0 > PULL_CLOSE || speed > FLICK_CLOSE) {
      // the exit animation starts from the current position (only its end is set) and takes the sheet down
      close();
      return;
    }
    // back up on the spatial spring; animation stays none, or the entry animation would start over
    body.style.transition = 'transform var(--dur-spatial) var(--spring-spatial)';
    body.style.transform = '';
    body.addEventListener(
      'transitionend',
      () => {
        body.style.transition = '';
      },
      { once: true },
    );
  };

  return (
    <dialog
      ref={ref}
      className={`sheet sheet-${variant}`}
      aria-labelledby={titleId}
      data-testid={testId}
      data-closing={closing || undefined}
      onCancel={(e) => {
        // Escape: the sheet closes with its animation, then the parent unmounts it
        e.preventDefault();
        close();
      }}
      onClick={(e) => {
        // a tap on the backdrop closes a bottom sheet (not the full screen flow, not an alert)
        if (variant === 'sheet' && e.target === e.currentTarget) close();
      }}
    >
      <div
        ref={bodyRef}
        className="sheet-body"
        tabIndex={-1}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerEnd}
        onPointerCancel={onPointerEnd}
        onClickCapture={(e) => {
          // the click that ends a pull is not a tap on what lies under the finger
          if (swallowClick.current) {
            swallowClick.current = false;
            e.stopPropagation();
          }
        }}
      >
        {variant === 'sheet' && <span className="sheet-grab" aria-hidden="true" />}
        {variant === 'sheet' ? (
          // a bottom sheet (prototype "Konto"): the title at the left, closing at the right
          <header className="sheet-head sheet-head-start">
            <h2 id={titleId} className={hideTitle ? 'visually-hidden' : 'sheet-title'}>
              {title}
            </h2>
            {headerAside}
            {closeLabel && (
              <IconButton icon="close" label={closeLabel} onClick={close} data-testid={closeTestId} />
            )}
          </header>
        ) : (
          <header className="sheet-head">
            {closeLabel ? (
              <IconButton icon="close" label={closeLabel} onClick={close} data-testid={closeTestId} />
            ) : (
              <span className="sheet-head-spacer" />
            )}
            <h2 id={titleId} className={hideTitle ? 'visually-hidden' : 'sheet-title'}>
              {title}
            </h2>
            {headerAside ?? <span className="sheet-head-spacer" />}
          </header>
        )}
        {children}
      </div>
    </dialog>
  );
}
